import type { InviteAcceptInput, LoginInput } from '@hr/shared';
import type { inferRouterOutputs } from '@trpc/server';
import { uk } from '@/i18n/uk';
import { queryClient, trpc, type AppRouter } from './trpc';

type Me = NonNullable<inferRouterOutputs<AppRouter>['auth']['me']>;

export class LoginError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export async function login(input: LoginInput) {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  });
  const body = (await res.json().catch(() => ({}))) as { message?: string } & Partial<Me>;
  if (!res.ok) throw new LoginError(body.message ?? uk.login.failed, res.status);
  // Записуємо користувача в кеш одразу: guard роуту читає його через ensureQueryData.
  queryClient.setQueryData(trpc.auth.me.queryKey(), body as Me);
}

/** Хто запрошений за посиланням; null — посилання недійсне. */
export async function fetchInvite(
  token: string,
): Promise<{ email: string; fullName: string } | null> {
  const res = await fetch(`/api/auth/invite/${encodeURIComponent(token)}`, {
    credentials: 'same-origin',
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(uk.common.error);
  return (await res.json()) as { email: string; fullName: string };
}

/** Пароль за запрошенням; сервер одразу відкриває сесію. */
export async function acceptInvite(input: InviteAcceptInput) {
  const res = await fetch('/api/auth/invite/accept', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  });
  const body = (await res.json().catch(() => ({}))) as { message?: string } & Partial<Me>;
  if (!res.ok) throw new LoginError(body.message ?? uk.common.error, res.status);
  queryClient.setQueryData(trpc.auth.me.queryKey(), body as Me);
}

export async function logout() {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
  queryClient.clear();
}
